import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Form,
  Dropdown,
  Modal,
} from "react-bootstrap";
import { IoEye } from "react-icons/io5";
import { VscDiffAdded } from "react-icons/vsc";
import { FaEdit } from "react-icons/fa";
import { FcApproval } from "react-icons/fc";
import { TbCalendarCancel } from "react-icons/tb";
import { Link } from "react-router-dom";
import "./total-leave.css";
import LeaveLayout from "../../components/LeaveLayout.jsx";
import axios from "axios";

export default function TotalLeave() {
  const rowsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({});
  const [totalEntries, setTotalEntries] = useState(0);
  const [leaves, setLeaves] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [searchQuery, setSearchQuery] = useState("");
  const [limit] = useState(10);

  // Helper function to get Leave Type label
  const getLeaveTypeLabel = (leave) => {
    const type = leave.allowanceType || leave.leaveType;
    switch (type?.toLowerCase()) {
      case 'yearly':
        return 'Yearly Allowance';
      case 'december':
        return 'December Holiday';
      case 'simple':
        return 'Simple Leave';
      default:
        return type || 'Unknown Type';
    }
  };

  // Helper function for badge color
  const getLeaveTypeBadge = (leave) => {
    const type = leave.allowanceType || leave.leaveType;
    switch (type?.toLowerCase()) {
      case 'yearly':
        return 'bg-info';
      case 'december':
        return 'bg-warning';
      case 'simple':
        return 'bg-secondary';
      default:
        return 'bg-dark';
    }
  };

  // Fetch all leaves
  const fetchLeaves = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `https://backend.ems.hiradentallab.com/api/leaves/leaves?search=${searchQuery}&page=${currentPage}&limit=${limit}`
      );
      
      if (response.data) {
        setLeaves(response.data.leaves || []);
        setTotalEntries(response.data.totalEntries || response.data.leaves?.length || 0);
      }
    } catch (error) {
      console.error("Error fetching leaves:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaves();
  }, [currentPage, searchQuery]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const updateLeaveStatus = async (leaveId, status) => {
    try {
      const role = localStorage.getItem("role");
      if (!role) {
        setShowAlert(true);
        setAlertMessage("Role is not available.");
        setAlertType("error");
        return;
      }

      const response = await axios.put(
        `https://backend.ems.hiradentallab.com/api/leaves/leave/${leaveId}/status`,
        { status },
        {
          headers: { role: role }
        }
      );

      if (response.status === 200) {
        setShowAlert(true);
        setAlertMessage(`Leave ${status} successfully!`);
        setAlertType("success");

        // Update state locally
        setLeaves((prevLeaves) =>
          prevLeaves.map((leave) =>
            leave._id === leaveId
              ? {
                  ...leave,
                  status: status,
                  adminAction: response.data.leave?.adminAction,
                }
              : leave
          )
        );

        setTimeout(() => setShowAlert(false), 3000);
      }
    } catch (error) {
      console.error("Error updating leave status:", error);
      setShowAlert(true);
      setAlertMessage("Error updating leave status. Please try again.");
      setAlertType("error");
      setTimeout(() => setShowAlert(false), 3000);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= Math.ceil(totalEntries / rowsPerPage)) {
      setCurrentPage(pageNumber);
    }
  };

  const pageRange = () => {
    const totalPages = Math.ceil(totalEntries / rowsPerPage);
    const pages = [];
    let startPage = Math.max(currentPage - 1, 1);
    let endPage = Math.min(startPage + 2, totalPages);

    if (endPage - startPage < 2) {
      startPage = Math.max(endPage - 2, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const handleShow = (data) => {
    setModalData(data);
    setShowModal(true);
  };

  const handleClose = () => setShowModal(false);

  return (
    <LeaveLayout>
      <div className="container-fluid">
        <h1 className="total-leave-header">
          <VscDiffAdded className="total-leave-icon" />
          Total Leave
        </h1>
        <hr />

        <div className="total-leave-content">
          {/* Alert */}
          {showAlert && (
            <div
              className={`alert ${alertType === "success" ? "alert-success" : "alert-danger"}`}
              role="alert"
            >
              {alertMessage}
            </div>
          )}

          <div className="filter-container">
            <Col xs={12} md={6} className="filter-left">
              <Dropdown className="d-inline mx-2" autoClose="inside">
                <Dropdown.Toggle id="dropdown-autoclose-inside">
                  Show Entries
                </Dropdown.Toggle>
                <Dropdown.Menu className="custom-dropdown-menu">
                  <Dropdown.Item href="#">15</Dropdown.Item>
                  <Dropdown.Item href="#">25</Dropdown.Item>
                  <Dropdown.Item href="#">50</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            <Col xs={12} md={6} className="filter-right">
              <Form>
                <Form.Control
                  type="search"
                  placeholder="Search by name or department..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </Form>
            </Col>
          </div>

          <div className="table-responsive">
            {loading ? (
              <p className="text-center p-4">Loading leaves...</p>
            ) : (
              <table className="table table-striped">
                <thead className="table-header">
                  <tr className="total-leave-table-header">
                    <th>SR</th>
                    <th>Name</th>
                    <th>Leave Type</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Applied Date</th>
                    <th>Status</th>
                    <th>HR Approval</th>
                    <th>Admin Approval</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {leaves.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center py-4">
                        No leaves found
                      </td>
                    </tr>
                  ) : (
                    leaves.map((leave, index) => (
                      <tr key={leave._id}>
                        <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                        <td>{leave.name || leave.username}</td>
                        <td>
                          <span className={`badge ${getLeaveTypeBadge(leave)}`}>
                            {getLeaveTypeLabel(leave)}
                          </span>
                        </td>
                        <td>
                          {new Date(leave.startDate).toLocaleDateString()}
                        </td>
                        <td>
                          {new Date(leave.endDate).toLocaleDateString()}
                        </td>
                        <td>
                          {new Date(leave.appliedDate || leave.createdAt).toLocaleDateString()}
                        </td>
                        <td>
                          <span className={`badge ${
                            leave.status?.toLowerCase() === 'approved' ? 'bg-success' :
                            leave.status?.toLowerCase() === 'rejected' ? 'bg-danger' : 'bg-warning'
                          }`}>
                            {leave.status || 'Pending'}
                          </span>
                        </td>
                        <td>{leave.hrAction || leave.hrApproval || '-'}</td>
                        <td>{leave.adminAction || leave.adminApproval || '-'}</td>
                        <td>
                          <div className="action-button d-flex gap-2">
                            <Link to={`/edit-leave/${leave._id}`}>
                              <Button variant="warning" size="sm">
                                <FaEdit />
                              </Button>
                            </Link>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleShow(leave)}
                            >
                              <IoEye />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {leaves.length > 0 && (
            <div className="pagination-row">
              <div className="entries-info">
                Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
                {Math.min(currentPage * rowsPerPage, leaves.length)} of{" "}
                {totalEntries} entries
              </div>

              <nav>
                <ul className="pagination no-gap">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <a
                      href="/#"
                      className="page-link"
                      onClick={(e) => { e.preventDefault(); handlePageChange(currentPage - 1); }}
                    >
                      Previous
                    </a>
                  </li>

                  {pageRange().map((pageNumber) => (
                    <li
                      key={pageNumber}
                      className={`page-item ${currentPage === pageNumber ? "active" : ""}`}
                    >
                      <a
                        href="/#"
                        className="page-link"
                        onClick={(e) => { e.preventDefault(); handlePageChange(pageNumber); }}
                      >
                        {pageNumber}
                      </a>
                    </li>
                  ))}

                  <li
                    className={`page-item ${
                      currentPage === Math.ceil(totalEntries / rowsPerPage) ? "disabled" : ""
                    }`}
                  >
                    <a
                      href="/#"
                      className="page-link"
                      onClick={(e) => { e.preventDefault(); handlePageChange(currentPage + 1); }}
                    >
                      Next
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>

        {/* Leave Details Modal */}
        <Modal show={showModal} onHide={handleClose} centered>
          <Modal.Header closeButton>
            <Modal.Title>Leave Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalData && (
              <>
                <p><strong>Name:</strong> {modalData.name || modalData.username}</p>
                <p>
                  <strong>Leave Type:</strong>{" "}
                  {getLeaveTypeLabel(modalData)}
                </p>
                <p>
                  <strong>Start Date:</strong>{" "}
                  {new Date(modalData.startDate).toLocaleDateString()}
                </p>
                <p>
                  <strong>End Date:</strong>{" "}
                  {new Date(modalData.endDate).toLocaleDateString()}
                </p>
                <p>
                  <strong>Applied Duration:</strong>{" "}
                  {modalData.appliedDuration || modalData.includedDates?.length || 0} day(s)
                </p>
                <p>
                  <strong>Applied Date:</strong>{" "}
                  {new Date(modalData.appliedDate || modalData.createdAt).toLocaleDateString()}
                </p>
                <p>
                  <strong>HR Approval:</strong> {modalData.hrAction || modalData.hrApproval || '-'}
                </p>
                <p>
                  <strong>Admin Approval:</strong> {modalData.adminAction || modalData.adminApproval || '-'}
                </p>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={handleClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </LeaveLayout>
  );
}